import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Cpu } from "lucide-react";
import api, { TOKEN_KEY } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const MAX_RETRIES = 3;

export default function AuthCallback() {
    const navigate = useNavigate();
    const { setUser } = useAuth();
    const processed = useRef(false);
    const [status, setStatus] = useState("AUTENTICANDO…");
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (processed.current) return;
        processed.current = true;
        const hash = window.location.hash || "";
        const match = hash.match(/session_id=([^&]+)/);
        const sessionId = match ? decodeURIComponent(match[1]) : null;
        if (!sessionId) {
            navigate("/login", { replace: true });
            return;
        }
        const exchange = async (attempt) => {
            try {
                const { data } = await api.post("/auth/session", { session_id: sessionId });
                if (data.session_token) localStorage.setItem(TOKEN_KEY, data.session_token);
                setUser(data.user);
                window.history.replaceState({}, document.title, "/");
                navigate("/", { replace: true, state: { user: data.user } });
            } catch (e) {
                const isNetwork = !e.response;
                if (isNetwork && attempt < MAX_RETRIES) {
                    setStatus(`SIN CONEXIÓN CON EL SERVIDOR — REINTENTANDO (${attempt + 1}/${MAX_RETRIES})…`);
                    setTimeout(() => exchange(attempt + 1), 2000 * (attempt + 1));
                    return;
                }
                setFailed(true);
                setStatus(
                    isNetwork
                        ? "Sin conexión con el servidor. Revisa tu internet o cambia de red (WiFi/datos) e intenta de nuevo."
                        : "La sesión de Google expiró o ya fue usada. Vuelve a iniciar sesión."
                );
                setTimeout(() => navigate("/login", { replace: true }), 4000);
            }
        };
        exchange(0);
    }, [navigate, setUser]);

    return (
        <div className="grain flex min-h-screen items-center justify-center bg-background text-foreground">
            <div className="flex max-w-md flex-col items-center gap-4 px-6 text-center">
                <div
                    className={`flex h-12 w-12 items-center justify-center border ${failed ? "border-destructive text-destructive" : "animate-pulse border-primary text-primary"}`}
                >
                    <Cpu size={22} />
                </div>
                <div className="font-head text-sm tracking-widest" data-testid="auth-callback-status">
                    {status}
                </div>
            </div>
        </div>
    );
}
